'use client';

import { useMemo, useState } from 'react';
import type { ShowDetailRecord } from '../../_lib/detail/show-detail-types';
import { buildWeeklySchedule } from '../../_lib/detail/formatters';
import { InfoTab } from './InfoTab';
import { ScheduleTab } from './ScheduleTab';
import { AccessibilityTab } from './AccessibilityTab';
import { ReviewsTab } from './ReviewsTab';

interface ShowDetailTabsProps {
  show: ShowDetailRecord;
  /** Resolved once by the server and threaded down — see
   *  `formatters.ts`'s `getDefaultSelectedDate` doc comment for why
   *  `buildWeeklySchedule` can't derive "today" via `new Date()` inside
   *  this `'use client'` tree without risking a hydration mismatch. */
  todayKey: string;
}

type TabKey = 'info' | 'schedule' | 'accessibility' | 'reviews';

/**
 * Tabs Section shell — brd-frontend.md §6, brd-backend.md §6, frd.md
 * FR-TAB-01..07. Computes which of Info/Schedule/Accessibility should even
 * render (Reviews is never hidden — FR-TAB-05) and auto-selects the first
 * available tab, matching the reference's `DOM.tabContainer.children().eq(0
 * ).click()` on load.
 */
export function ShowDetailTabs({ show, todayKey }: ShowDetailTabsProps) {
  const { event, performances, reviews } = show;

  const hasInfo = !!event.description;
  const hasSchedule = useMemo(() => buildWeeklySchedule(performances, todayKey).hasAny, [performances, todayKey]);
  const hasAccessibility = !!(event.contentWarnings || event.accessibility);

  const tabs = useMemo(() => {
    const list: { key: TabKey; label: string }[] = [];
    if (hasInfo) list.push({ key: 'info', label: 'Info' });
    list.push({ key: 'schedule', label: 'Schedule' });
    if (hasAccessibility) list.push({ key: 'accessibility', label: 'Accessibility' });
    list.push({ key: 'reviews', label: 'Reviews' });
    return list.filter((t) => t.key !== 'schedule' || hasSchedule);
  }, [hasInfo, hasSchedule, hasAccessibility]);

  const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? 'reviews');
  const activeKey = tabs.some((t) => t.key === active) ? active : tabs[0]?.key ?? 'reviews';

  return (
    <section className="shows-detail-card shows-detail-tabs-card">
      <div className="shows-detail-tab-header" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeKey === t.key}
            className={`shows-detail-tab-btn ${activeKey === t.key ? 'active' : ''}`}
            onClick={() => setActive(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="shows-detail-tab-body">
        {activeKey === 'info' && <InfoTab description={event.description} />}
        {activeKey === 'schedule' && <ScheduleTab performances={performances} todayKey={todayKey} />}
        {activeKey === 'accessibility' && (
          <AccessibilityTab contentWarnings={event.contentWarnings} accessibility={event.accessibility} />
        )}
        {activeKey === 'reviews' && <ReviewsTab reviews={reviews} />}
      </div>
    </section>
  );
}
